import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { ProjectService } from 'src/app/core/services/project.service';
import {
  AreasWithStackingGroup,
  FreeSpace,
  Group,
  ProjectOptions,
  Session,
} from 'src/app/core/types';
import {
  AmbientLight,
  Camera,
  DirectionalLight,
  HemisphereLight,
  LineSegments,
  Vector3,
  MeshLambertMaterial,
  DoubleSide,
} from 'three';
import { Area } from './area';
import { AreasCalculator } from './areas-calculator';
import {
  DefaultPerspectiveCameraManager,
  OrthographicCameraManager,
  PrintPerspectiveCameraManager,
} from './camera-managers';
import { Ground } from './ground';
import { Load } from './load';
import { LoadDrag } from './load-drag';
import { LoadOutline } from './load-outline';
import { LoadingSpace } from './loading-space';
import { LoadingSpaceFactory } from './loading-space-factory';
import { LoadsGroupsDrawer } from './loads-groups-drawer';
import { sessionGroupsToLoadGroups } from './session-load-groups';
import { MouseActions } from './mouse-actions';
import { Outside } from './outside';
import RenderingContext from './rendering-context';
import { CookieService } from 'ngx-cookie-service';
import { Unloading, UnloadingGround } from './unloading';
import { SharedProjectService } from '../../core/services/shared-project.service';
import { Message, MessageService } from '../../core/services/message.service';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MainView implements OnDestroy {
  public viewType: string;
  public loadsGroupsDrawer: LoadsGroupsDrawer;
  public loadingSpaceFactory: LoadingSpaceFactory = new LoadingSpaceFactory();
  public cameraManager: any;
  public loadDrag: LoadDrag;
  public loadingSpace: LoadingSpace;
  public outside: Outside;
  public unloading: Unloading;
  public loads: Load[] = [];
  public ground: LineSegments;
  public unloadingGround: LineSegments;
  public renderingContext: RenderingContext<any>;
  public areasCalculator: AreasCalculator;
  public areas: Area[] = [];
  public element: HTMLElement;
  public areasWithStackingGroups: AreasWithStackingGroup[];
  public loadOutline: LoadOutline;
  public mouseActions: MouseActions;
  public isVarioFloorOn = false;
  private isSharedView = false;
  public isAnimating = null;
  private timeoutSet = false;
  public timeoutHook = null;
  private animationDelay = 500;
  private subscription: Subscription;

  constructor(
    private domElement: HTMLElement,
    session: Session,
    private projectService?: ProjectService,
    printMode?: boolean,
    projectOptions?: ProjectOptions,
    private zone?: NgZone,
    private cookieService?: CookieService,
    private sharedProjectService?: SharedProjectService,
    private messageService?: MessageService,
  ) {
    this.areasCalculator = new AreasCalculator();
    this.initailize(domElement, session, printMode, projectOptions);
    if (this.messageService) {
      this.subscription = this.messageService.getMessages().subscribe((message: Message): void => {
        if (message.name === 'startShortAnimation') {
          this.startShortAnimation();
        }
      });
    }
  }

  public setSession(session: Session, printMode?: boolean, projectOptions?: ProjectOptions): void {
    const loadingSpace = this.loadingSpaceFactory.createLoadingSpace(session.truck, session.unit);
    if (!printMode && session.outside.dimensions && session.outside.position) {
      this.outside = new Outside(session.outside.dimensions, session.outside.position as Vector3);
    } else {
      this.outside = undefined;
    }

    if (!printMode && session.unloadingSpace) {
      this.unloading = new Unloading(
        session.unloadingSpace.dimensions,
        session.unloadingSpace.position as Vector3,
      );
    } else {
      this.unloading = undefined;
    }

    if (this.unloadingGround && !session.unloadingSpace) {
      this.removeUnloadingGround();
    }
    if (this.loadingSpace && !this.loadingSpace.isLoadingSpaceSame(loadingSpace)) {
      const isLoadChange =
        !this.loadingSpace.isLoadingSpaceSameWithoutAxis(loadingSpace) ||
        !this.loadingSpace.isLoadingSpaceNameSame(loadingSpace);
      this.removeLoadingSpace();
      this.loadingSpace = this.drawLoadingSpace(loadingSpace, printMode);
      this.loadsGroupsDrawer.setLoadingSpace(this.loadingSpace);
      if (this.ground) {
        this.removeGround();
      }
      if (!printMode) {
        this.ground = this.drawGround(this.outside, session);
      }
      if (isLoadChange && !printMode) {
        (this.cameraManager as DefaultPerspectiveCameraManager).resetCamera(
          this.loadingSpace,
          session.unit,
        );
      }
    }

    if (!printMode && this.unloading) {
      this.removeUnloadingGround();
      this.unloadingGround = this.drawUnloadingGround(this.unloading);
    }

    if (session.groups) {
      const unloading: Group[] =
        session.unloadingSpace !== undefined ? session.unloadingSpace.groups : [];
      const sessionGroups: Group[] = [...session.groups, ...unloading];
      this.loads = this.drawLoads(sessionGroups, session.unit);
      this.checkStackedStatusForLoads(this.loads);
    }
    if (session.areas) {
      this.areas = this.createAreas(session);
      // this.addFreeSpacesHelperBoxes(this.areas);
      this.loadDrag.update(
        this.loads,
        this.areas,
        loadingSpace,
        this.outside,
        this.unloading,
        session.groups,
        this.areasWithStackingGroups,
      );
    }
    if (!printMode && this.projectService && projectOptions?.backlightOn) {
      if (!this.loadOutline || !this.loadOutline.isActive) {
        this.loadOutline = new LoadOutline(
          this.renderingContext,
          this.loads,
          this.projectService,
          this.zone,
          this,
        );
      }
      this.loadOutline.update(this.renderingContext, this.loads);
    } else if (this.loadOutline) {
      this.loadOutline.disableOutline();
    }

    if (!printMode && projectOptions?.measure) {
      if (!this.renderingContext.scene.getObjectByName('Measure')) {
        this.loadingSpace.showMeasure(this.renderingContext.scene);
        this.mouseActions.activate();
      }
    } else {
      if (!this.isSharedView) {
        this.loadingSpace.hideMeasure(this.renderingContext.scene);
      }
      this.mouseActions.deactivate();
    }
    this.startShortAnimation(this.animationDelay);
  }

  public hideFloor() {
    this.ground.visible = false;
  }

  public showFloor() {
    this.ground.visible = true;
  }

  public disableSnapping() {
    this.loadDrag.deactivateSnapper();
  }

  public enableSnapping() {
    this.loadDrag.activateSnapper();
  }

  public hideLoadsName() {
    const loads = this.renderingContext.scene.getObjectByName('loadsGroup');
    if (loads) {
      loads.children.forEach((load: Load) => {
        load.material = new MeshLambertMaterial({ color: load.color, side: DoubleSide });
        const loadName = load.getObjectByName('loadName');
        if (loadName) {
          loadName.visible = false;
        }
      });
    }
  }

  public showLoadsName() {
    const loads = this.renderingContext.scene.getObjectByName('loadsGroup');
    if (loads) {
      loads.children.forEach((load) => {
        const loadName = load.getObjectByName('loadName');
        if (loadName) {
          loadName.visible = true;
        }
      });
    }
  }

  public hideOutsideLoads() {
    this.loads.forEach((load) => {
      if (load.packed === undefined) {
        load.visible = false;
      }
    });
  }

  public showOutsideLoads() {
    this.loads.forEach((load) => {
      if (load.packed === undefined) {
        load.visible = true;
      }
    });
  }

  public setTopViewIn2D() {
    if (this.viewType !== 'ortographic') {
      this.loadingSpace.hideAxles();

      this.cameraManager = new OrthographicCameraManager(
        this.renderingContext,
        this.loadingSpace,
        this.domElement,
        this.zone,
      );
    }
    this.loadingSpace.showBackDoors();
    this.hideLoadsName();
    this.viewType = 'ortographic';
    (this.cameraManager as OrthographicCameraManager).viewFromTopSide();
    this.renderingContext.renderer.render(
      this.renderingContext.scene,
      this.renderingContext.camera as Camera,
    );
  }

  public setLeftViewIn2D() {
    if (this.viewType !== 'ortographic') {
      this.loadingSpace.hideAxles();

      this.cameraManager = new OrthographicCameraManager(
        this.renderingContext,
        this.loadingSpace,
        this.domElement,
        this.zone,
      );
    }
    this.loadingSpace.hideBackDoors();
    this.hideLoadsName();
    this.viewType = 'ortographic';
    (this.cameraManager as OrthographicCameraManager).viewFromLeftSide();
    this.renderingContext.renderer.render(
      this.renderingContext.scene,
      this.renderingContext.camera as Camera,
    );
  }

  public setRightViewIn2D() {
    if (this.viewType !== 'ortographic') {
      this.cameraManager = new OrthographicCameraManager(
        this.renderingContext,
        this.loadingSpace,
        this.domElement,
        this.zone,
      );
    }
    this.loadingSpace.hideBackDoors();
    this.hideLoadsName();
    this.viewType = 'ortographic';
    (this.cameraManager as OrthographicCameraManager).viewFromRightSide();
    this.renderingContext.renderer.render(
      this.renderingContext.scene,
      this.renderingContext.camera as Camera,
    );
  }

  public setPrintPerspectiveCamera(showLoadName: boolean) {
    if (this.viewType !== 'perspective') {
      this.cameraManager = new PrintPerspectiveCameraManager(
        this.renderingContext,
        this.loadingSpace,
        this.domElement,
        this.zone,
      );
    }
    if (showLoadName) {
      this.showLoadsName();
    } else {
      this.hideLoadsName();
    }
    this.viewType = 'perspective';
    this.loadingSpace.showFront();
    this.loadingSpace.hideBackDoors();
    this.renderingContext.renderer.render(
      this.renderingContext.scene,
      this.renderingContext.camera as Camera,
    );
  }

  public getImageData(hideFloor?: boolean) {
    if (hideFloor) {
      this.hideFloor();
      this.hideOutsideLoads();
      this.renderingContext.renderer.render(
        this.renderingContext.scene,
        this.renderingContext.camera as Camera,
      );
      const image = this.renderingContext.renderer.domElement.toDataURL('image/png');
      this.showFloor();
      this.showOutsideLoads();
      return image;
    } else {
      return this.renderingContext.renderer.domElement.toDataURL('image/png');
    }
  }

  public disableDrag() {
    this.loadDrag.deactivateDrag();
  }

  public enableDrag() {
    this.loadDrag.activateDrag();
  }

  public disableMouseActions() {
    this.mouseActions.deactivate();
  }

  public enableMouseActions() {
    this.mouseActions.activate();
  }

  public resetView() {
    this.startShortAnimation(this.animationDelay);
    (this.cameraManager as DefaultPerspectiveCameraManager).resetCamera();
  }

  public onWindowResize() {
    this.startShortAnimation(this.animationDelay);
    (this.cameraManager as DefaultPerspectiveCameraManager).onWindowResize();
  }

  public loseContext() {
    this.renderingContext.renderer.getContext().getExtension('WEBGL_lose_context').loseContext();
  }

  public disableCameraBox() {
    (this.cameraManager as DefaultPerspectiveCameraManager).disableCameraBox();
  }

  private createRenderingContext(domElement: HTMLElement) {
    return RenderingContext.get(domElement);
  }

  private initailize(
    domElement: HTMLElement,
    session: Session,
    printMode: boolean,
    projectOptions: ProjectOptions,
  ): void {
    this.sharedProjectService.isViewingShared.subscribe((isViewingShared: boolean): void => {
      this.isSharedView = isViewingShared;
    });
    this.element = domElement;
    this.renderingContext = this.createRenderingContext(domElement);
    this.addGlobalLight();
    this.addDirectionalLight();
    if (printMode) {
      this.addDirectionalLight(false);
    }
    if (session.truck) {
      const loadingSpace = this.loadingSpaceFactory.createLoadingSpace(session.truck, session.unit);
      this.loadingSpace = this.drawLoadingSpace(loadingSpace, printMode);
    }
    this.loadsGroupsDrawer = new LoadsGroupsDrawer(printMode, this.loadingSpace);
    if (!printMode && session.outside?.dimensions && session.outside.position) {
      this.outside = new Outside(session.outside.dimensions, session.outside.position as Vector3);
      this.ground = this.drawGround(this.outside, session);
    }

    if (!printMode && session.unloadingSpace) {
      this.unloading = new Unloading(
        session.unloadingSpace.dimensions,
        session.unloadingSpace.position as Vector3,
      );
      this.unloadingGround = this.drawUnloadingGround(this.unloading);
    }

    if (session.areas) {
      this.areas = this.createAreas(session);
    }
    if (session.groups) {
      this.loads = this.drawLoads(session.groups, session.unit);
      this.checkStackedStatusForLoads(this.loads);
    }

    if (printMode) {
      this.viewType = 'perspective';
      this.cameraManager = new PrintPerspectiveCameraManager(
        this.renderingContext,
        this.loadingSpace,
        domElement,
        this.zone,
      );
    } else {
      this.viewType = 'perspective';
      this.cameraManager = new DefaultPerspectiveCameraManager(
        this.loadingSpace,
        domElement,
        this.renderingContext,
        session.unit,
        this.zone,
        this.sharedProjectService,
        this,
      );
    }
    this.loadDrag = new LoadDrag(
      this.renderingContext,
      this.areas,
      this.loads,
      this.loadingSpace,
      this.outside,
      this.unloading,
      session.groups,
      this.areasWithStackingGroups,
      this.zone,
      this.cookieService,
      this.projectService,
      this,
    );
    this.loadDrag.update(
      this.loads,
      this.areas,
      this.loadingSpace,
      this.outside,
      this.unloading,
      session.groups,
      this.areasWithStackingGroups,
    );

    if (!printMode && this.projectService && projectOptions.backlightOn) {
      this.loadOutline = new LoadOutline(
        this.renderingContext,
        this.loads,
        this.projectService,
        this.zone,
        this,
      );
      this.loadOutline.update(this.renderingContext, this.loads);
    }

    this.mouseActions = new MouseActions(this.renderingContext, this.zone, this);
    this.mouseActions.activate();
    if (!printMode && projectOptions.measure) {
      this.loadingSpace.showMeasure(this.renderingContext.scene);
    } else {
      if (!this.isSharedView) {
        this.loadingSpace.hideMeasure(this.renderingContext.scene);
      }
      this.mouseActions.deactivate();
    }
    this.animate();
  }

  private addDirectionalLight(isFront = true): void {
    const directionalLight = new DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(isFront ? -25000 : 25000, 10000, -15000);
    directionalLight.name = isFront ? 'directionalLightFront' : 'directionalLightBack';
    this.renderingContext.scene.add(directionalLight);
  }

  private addGlobalLight(): void {
    const hemisphereLight = new HemisphereLight(0xffffff, 0xffffff, 2.0);
    hemisphereLight.name = 'hemiLight';
    this.renderingContext.scene.add(hemisphereLight);

    const ambientLight = new AmbientLight(0xffffff, 0.8);
    ambientLight.name = 'ambientLight';
    this.renderingContext.scene.add(ambientLight);
  }

  private drawLoadingSpace(loadingSpace: LoadingSpace, printMode?: boolean): LoadingSpace {
    loadingSpace.draw(this.renderingContext, printMode);
    return loadingSpace;
  }

  private drawLoads(groups: Group[], units: string): Load[] {
    const isShadows: boolean =
      (this.cookieService && this.cookieService.get('shadows').includes('true')) === undefined ||
      (this.cookieService && this.cookieService.get('shadows').includes('true'));
    return this.loadsGroupsDrawer.drawGroups(
      sessionGroupsToLoadGroups(groups),
      this.renderingContext.scene,
      units,
      isShadows,
    ) as Load[];
  }

  private drawGround(outside: Outside, session: Session): LineSegments {
    if (outside) {
      const ground = new Ground();
      return ground.draw(this.renderingContext.scene, outside, session);
    }
  }

  private drawUnloadingGround(unloading: Unloading): LineSegments {
    if (unloading) {
      const unloadingGround = new UnloadingGround();
      return unloadingGround.draw(this.renderingContext.scene, unloading);
    }
  }

  private checkStackedStatusForLoads(loads: Load[]) {
    const notStackedLoads = loads.filter((load) => !load.stacked);
    notStackedLoads.forEach((notStackedLoad) => {
      let withPivot = notStackedLoad.name.includes('container')
        ? (notStackedLoad.children[0].children[0] as Load)
        : notStackedLoad;
      withPivot = notStackedLoad.name.includes('pivot')
        ? (notStackedLoad.children[0] as Load)
        : withPivot;
      return withPivot !== undefined ? withPivot.setStacked(loads) : false;
    });
  }

  private createAreas(session: Session): Area[] {
    const areaObjects: Area[] = [];
    session.areas.forEach((a: FreeSpace) => {
      const area: Area = new Area(
        a.dimensions.length,
        a.dimensions.width,
        a.dimensions.height,
        a.position.x,
        a.position.y,
        a.position.z,
        a.type,
      );
      area.times100();
      areaObjects.push(area);
    });
    this.areasWithStackingGroups = this.areasCalculator.generateAreasStackingGroups(
      areaObjects,
      session.groups,
    );
    return areaObjects;
  }

  private removeLoadingSpace() {
    this.loadingSpace.remove(this.renderingContext.scene);
  }

  private removeGround() {
    this.renderingContext.scene.remove(this.ground);
    this.ground.geometry.dispose();
  }

  private removeUnloadingGround() {
    this.renderingContext.scene.remove(this.unloadingGround);
    this.unloadingGround?.geometry.dispose();
  }

  public startAnimation(): void {
    clearTimeout(this.timeoutHook);
    if (this.isAnimating === null || this.isAnimating === false) {
      this.isAnimating = true;
      this.animate();
    }
  }

  public stopAnimation(): void {
    this.isAnimating = false;
  }

  public startShortAnimation(length = 500): void {
    if (this.isAnimating === null || this.isAnimating === false) {
      this.startAnimation();
      this.timeoutSet = true;
      this.timeoutHook = setTimeout(() => {
        this.stopAnimation();
      }, length);
    }
  }

  public animate = () => {
    if (this.isAnimating) {
      this.renderingContext.renderer.render(
        this.renderingContext.scene,
        this.renderingContext.camera as Camera,
      );
      requestAnimationFrame(this.animate);
    }
    if (this.isAnimating === null && !this.timeoutSet) {
      this.renderingContext.renderer.render(
        this.renderingContext.scene,
        this.renderingContext.camera as Camera,
      );
      requestAnimationFrame(this.animate);
      this.timeoutSet = true;
      this.timeoutHook = setTimeout(() => {
        this.isAnimating = false;
        this.timeoutSet = false;
      }, this.animationDelay);
    }
  };

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
